/**
 * Categoría de plural para un número de semanas, en la única forma que sirve para los 7 idiomas del
 * producto.
 *
 * No se usan los sufijos de plural de i18next porque `recognitionLocales.test.ts` exige paridad
 * EXACTA de claves entre locales, y el inglés solo tiene dos categorías mientras el rumano necesita
 * tres. Con claves explícitas la paridad se mantiene y cada idioma escribe lo suyo.
 *
 * - `one`  → 1.
 * - `few`  → 2-19 (y las decenas cuyas dos últimas cifras caen ahí: 101-119).
 * - `many` → 0 y 20 en adelante, salvo la excepción anterior. El rumano exige aquí la preposición
 *            "de": «20 **de** săptămâni», «0 **de** săptămâni». Los demás idiomas repiten su forma
 *            plural normal. El 0 solo es alcanzable desde Gestión, si un administrador lo escribe:
 *            el catálogo que sirve el widget filtra los umbrales que no son mayores que cero.
 *
 * Vive aparte porque el mismo texto se pinta en dos sitios —el widget de racha y la pantalla de
 * Gestión— y el defecto ya apareció dos veces: primero como "1 semanas" y después como
 * "20 săptămâni". Una sola implementación, una sola prueba.
 */
export type WeeksPlural = 'one' | 'few' | 'many';

export function weeksPlural(weeks: number): WeeksPlural {
  if (weeks === 1) return 'one';
  if (weeks === 0) return 'many';
  const lastTwo = Math.abs(weeks) % 100;
  return weeks >= 20 && (lastTwo === 0 || lastTwo >= 20) ? 'many' : 'few';
}

/** Elige la clave i18n de una familia `{one, few, many}` según el número de semanas. */
export function weeksKeyOf(weeks: number, keys: Record<WeeksPlural, string>): string {
  return keys[weeksPlural(weeks)];
}
